// API client para apps/lead_magnets/ (Django backend).
// Multi-tenant: header X-Org-Id desde localStorage.org_id como fallback
// cuando el JWT no carga org_id (caso landing pública sin login).

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  EmailSequence,
  EmailSequenceCreateIn,
  LeadMagnet,
  LeadMagnetCreateIn,
  LeadMagnetListFilters,
  LeadMagnetListResponse,
  PublicLanding,
  SubscriberListResponse,
} from './types';

const DJANGO_PROXY = '/django-api';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class LeadMagnetsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'LeadMagnetsApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new LeadMagnetsApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const leadMagnetsApi = {
  // ── Admin ────────────────────────────────────────────────
  create: (data: LeadMagnetCreateIn): Promise<LeadMagnet> =>
    request<LeadMagnet>('/api/v1/lead-magnets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  list: (filters: LeadMagnetListFilters = {}): Promise<LeadMagnetListResponse> => {
    const params = new URLSearchParams();
    if (filters.active_only) params.set('active_only', 'true');
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request<LeadMagnetListResponse>(
      `/api/v1/lead-magnets${qs ? `?${qs}` : ''}`,
    );
  },

  listSubscribers: (
    magnetId: number,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<SubscriberListResponse> => {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request<SubscriberListResponse>(
      `/api/v1/lead-magnets/${magnetId}/subscribers${qs ? `?${qs}` : ''}`,
    );
  },

  createSequence: (
    magnetId: number,
    data: EmailSequenceCreateIn,
  ): Promise<EmailSequence> =>
    request<EmailSequence>(`/api/v1/lead-magnets/${magnetId}/sequences`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ── Public (sin auth) — usado para preview de landing ────
  getPublicLanding: (slug: string): Promise<PublicLanding> =>
    request<PublicLanding>(`/api/v1/public/lm/${encodeURIComponent(slug)}`),
};

// CSV export helper — corre client-side, no toca backend.
export function subscribersToCSV(subs: SubscriberListResponse['items']): string {
  const header = ['id', 'email', 'name', 'captured_at', 'consent_at'];
  const rows = subs.map((s) => [
    String(s.id),
    JSON.stringify(s.email),
    JSON.stringify(s.name || ''),
    s.captured_at || '',
    s.consent_at || '',
  ]);
  return [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

export function downloadCSV(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
